import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { StarDisplay } from "@/components/reviews/Stars";
import { fetchPublicReviews } from "@/lib/api";

interface FeaturedReview {
  id: string;
  name: string;
  company?: string | null;
  rating: number;
  review: string;
  city?: string | null;
}

export const FeaturedReviews = () => {
  const { t } = useLanguage();
  const [reviews, setReviews] = useState<FeaturedReview[]>([]);

  useEffect(() => {
    fetchPublicReviews(true)
      .then((r) => setReviews(r.data.reviews || []))
      .catch(() => setReviews([]));
  }, []);

  if (reviews.length === 0) return null;

  return (
    <section
      id="testimonials"
      data-testid="featured-reviews-section"
      className="border-t border-white/10 bg-ink py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <SectionLabel text={t.reviews.homeLabel} testId="featured-reviews-label" />
            <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white md:text-6xl">
              {t.reviews.homeHeading}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              data-testid="featured-reviews-more-link"
              to="/reviews"
              className="group inline-flex items-center gap-2 text-sm font-black uppercase tracking-[0.15em] text-gold transition-colors duration-300 hover:text-golddark"
            >
              {t.reviews.label}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {reviews.slice(0, 3).map((r, i) => (
            <Reveal key={r.id} delay={i * 0.1}>
              <article
                data-testid={`featured-review-${r.id}`}
                className="flex h-full flex-col border border-white/10 bg-charcoal p-8"
              >
                <StarDisplay rating={r.rating} />
                <p className="mt-5 flex-1 text-sm leading-relaxed text-neutral-300 md:text-base">
                  “{r.review}”
                </p>
                <p className="mt-6 border-t border-white/10 pt-5 font-display text-lg font-black tracking-tight text-white">
                  {r.name}
                  {r.company && (
                    <span className="ml-2 text-sm font-bold text-neutral-400">· {r.company}</span>
                  )}
                </p>
                {r.city && (
                  <p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-neutral-600">
                    {r.city}
                  </p>
                )}
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
